import React, { useState } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import { useUser } from "../../../store/useUser";
import { useSetHelpTopic } from "../../../store/useHelpTopics";
import CalAimDemographics from "./components/demographics";
import { CalAimForms } from "./components/calAIMForms";
import { EditCalAIM } from "./modals/EditCalAIM";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AddNoteCalAIM } from "./modals/AddNoteCalAIM";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { hasAnyPolicy } from "@/utils/policies";
import { 
  AlertCircle, 
  FileText, 
  Plus, 
  Edit, 
  Trash, 
  ClipboardList, 
  Calendar, 
  Activity, 
  Tag, 
  Clock, 
  FileSignature, 
  User, 
  AlertTriangle, 
  CheckCircle, 
  Building,
  StickyNote
} from "lucide-react";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const GET_CALAIM_DATA = gql`
  query GetCalAIMData($odrNumber: Int!) {
    getParticipantByODRNumber(odrnumber: $odrNumber) {
      ssn
      cin
      mrun
      cii
      lastInitialAssessmentDate {
        id
        date
      }
      lastLACAREScreeningDate {
        id
        date
      }
      lastLACAREReferralDate {
        id
        date
      }
      lastProgramCompletionDate: lastProgramCompletion {
        id
        date
      }
      wpc      
    }
  }
`;

const GET_CALAIM_NOTES = gql`
  query GetCalaimNotes($odrnumber: String!) {
    calaimNotes(odrnumber: $odrnumber) {
      id
      pos_code
      pos_name
      proceduremodifier
      proceduremod1
      proceduremod2
      proceduremod3
      proceduretype
      procedurelength
      note_entry_date
      procedurenote
      procedureentery
      entrydate
      entryby
    }
  }
`;

const UPDATE_PARTICIPANT = gql`
  mutation UpdateParticipantByODRNumber($odrnumber: Int!, $participant: Int, $updates: UpdateParticipantInput!) {
    updateParticipantByODRNumber(odrnumber: $odrnumber, participant: $participant, updates: $updates) {
      message
    }
  }
`;

const GET_HEALTHPLAN_PROVIDERS = gql`
  query GetHealthPlanProviders {
    getHealthPlanProviders {
      providerId
      providerName
    }
  }
`;

const DELETE_CALAIM_NOTE = gql`
  mutation DeleteCalaimNote($noteId: Int!) {
    deleteCalaimNote(noteId: $noteId) {
      message
    }
  }
`;

const LoadingState = () => (
  <div className="space-y-3">
    <Skeleton className="h-5 w-[180px]" />
    <div className="grid grid-cols-3 gap-3">
      <Skeleton className="h-[80px]" />
      <Skeleton className="h-[80px]" />
      <Skeleton className="h-[80px]" />
    </div>
  </div>
);

const ErrorState = () => (
  <Card className="bg-destructive/10">
    <CardContent className="p-3 flex items-center">
      <AlertTriangle className="h-3.5 w-3.5 mr-2 text-destructive" />
      <span className="text-xs text-destructive">Unable to load CalAIM data. Please try again.</span>
    </CardContent>
  </Card>
);

export const CalAIM = ({ odrNumber, programNumber }: { odrNumber: number; programNumber: number; }) => {
  // State declarations remain unchanged
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [selectedCalAIM, setSelectedCalAIM] = React.useState(null);
  const [formData, setFormData] = React.useState({
    calaim_pos: "",
    proceduremod1: "",
    proceduremod2: "",
    proceduremod3: "",
    proceduretype: "",
    procedurelength: "",
    note_entry_date: "",
    procedurenote: "",
    procedureentery: "",
  });
  const [newNoteModalOpen, setNewNoteModalOpen] = React.useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const [noteToDelete, setNoteToDelete] = React.useState<number | null>(null);
  const { data: healthPlanProvidersDataResult } = useQuery(GET_HEALTHPLAN_PROVIDERS);
  const { user } = useUser();
  const calaimNoteWritePolicies = ['program:participant:coordinatedcare~full', 'tools:billing:access~full'];
  const canCreateCalaimNote = hasAnyPolicy(user, calaimNoteWritePolicies);
  const canEditCalaimNote = hasAnyPolicy(user, calaimNoteWritePolicies);
  const canDeleteCalaimNote = hasAnyPolicy(user, calaimNoteWritePolicies);

  const { loading, error, data, refetch } = useQuery(GET_CALAIM_DATA, {
    variables: { odrNumber },
    skip: !odrNumber,
  });

  const { loading: loadingNotes, error: notesError, data: notesData, refetch: refetchNotes } = useQuery(GET_CALAIM_NOTES, {
    variables: { odrnumber: odrNumber.toString() },
    skip: !odrNumber,
  });

  const [updateParticipant] = useMutation(UPDATE_PARTICIPANT);
  const [deleteCalaimNote, { loading: deleteLoading }] = useMutation(DELETE_CALAIM_NOTE);

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleEditClick = (calaimNote: any) => {
    setSelectedCalAIM(calaimNote);
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setSelectedCalAIM(null);
  };

  const handleDeleteClick = (noteId: number) => {
    setNoteToDelete(noteId);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (noteToDelete === null) return;
    
    try {
      await deleteCalaimNote({
        variables: {
          noteId: noteToDelete
        }
      });
      refetchNotes();
      setIsDeleteDialogOpen(false);
      setNoteToDelete(null);
    } catch (error) {
      console.error("Error deleting CalAIM note:", error);
    }
  };

  useSetHelpTopic("ParticipantsCalAIM");

  const handleUpdateCalAIM = async (formData: any) => {
    try {
      const result = await updateParticipant({
        variables: {
          odrnumber: odrNumber,
          updates: {
            billingEvents: [
              {
                posCode: formData.calaim_pos,
                procedureModifier1: formData.proceduremod1,
                procedureModifier2: formData.proceduremod2,
                outreach: formData.proceduremod3,
                procedureType: formData.proceduretype,
                serviceUnits: parseInt(formData.procedurelength),
                serviceStart: formData.note_entry_date,
                timespentNote: formData.procedurenote,
                entryBy: formData.entryBy,
              },
            ],
          },
        },
      });
      const response = result.data.updateParticipantByODRNumber;
      if (response.responseCode === "SUCCESS") {
        console.log("Update successful:", response.message);
        handleCloseModal();
      } else {
        console.error("Update failed:", response.message);
      }
    } catch (error) {
      console.error("Error updating CalAIM:", error);
    }
  }

  if (loading) return <LoadingState />;
  if (error || !data) return <ErrorState />;

  const calaimData = data.getParticipantByODRNumber;
  // Ensure calaimNotes is always an array by using Array.isArray check
  const calaimNotes = Array.isArray(notesData?.calaimNotes) ? notesData.calaimNotes : [];
  const acuitys = { 1: "Low", 2: "Moderate", 4: "High" } as any;

  return (
    <div className="space-y-5 h-full flex flex-col">
      <div className="flex justify-between items-center flex-shrink-0">
        <div className="flex items-center gap-1.5">
          <ClipboardList className="h-4 w-4 text-gray-600" />
          <h1 className="text-base font-medium text-gray-800">CalAIM Status</h1>
        </div>
      </div>

      <div className="grid grid-cols-[20rem_1fr] gap-5 flex-shrink-0">
        <Card className="w-full shadow-sm">
          <CardHeader className="pb-1 pt-2 px-3">
            <div className="flex items-center gap-1.5">
              <Activity className="h-3.5 w-3.5 text-gray-500" />
              <h4 className="text-xs font-medium text-gray-700">ECM Acuity</h4>
            </div>
          </CardHeader>
          <CardContent className="pt-0 px-3 pb-2">
            <span className="text-xs text-gray-600">
              {acuitys[calaimData.acuity] || "Unknown"}
            </span>
          </CardContent>
        </Card>
        <CalAimForms calaimData={calaimData} odrNumber={odrNumber} />
      </div>

      <div className="flex-1 min-h-0 flex flex-col">
          <div className="flex items-center justify-between py-2 flex-shrink-0">
            <div className="flex items-center gap-1.5">
              <FileText className="h-3.5 w-3.5 text-gray-500" />
              <h3 className="text-xs font-medium text-gray-700">ECM CalAIM Notes</h3>
            </div>
            {canCreateCalaimNote && (
              <Button
                variant="outline"
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white border-0 h-7 px-2.5 text-xs"
                onClick={() => setNewNoteModalOpen(true)}
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Note
              </Button>
            )}
          </div>
          
          <div className="bg-white rounded-md border border-gray-200 flex-1 overflow-y-auto p-4 shadow-sm">
             <div className="space-y-1">
                {calaimNotes.length > 0 ? (
                    calaimNotes.map((note: any) => (
                        <div key={note.id} className="flex items-stretch py-2 group">
                          {/* Date Column */}
                          <div className="w-[80px] flex-shrink-0 text-right pr-3 pt-1">
                            <div className="text-xs font-medium text-gray-600 leading-none">
                               {note.note_entry_date}
                            </div>
                            <div className="text-xs text-gray-400 mt-1 truncate" title={note.entryby}>
                               {note.entryby}
                            </div>
                          </div>

                          {/* Timeline Marker */}
                          <div className="relative w-6 flex-shrink-0 flex justify-center">
                            <div className="absolute top-0 bottom-0 w-px bg-gray-200" />
                            <div className="h-2.5 w-2.5 rounded-full border-2 border-white shadow-sm z-10 bg-teal-500 mt-1.5" />
                          </div>

                          {/* Content Card */}
                          <div className="flex-1 ml-2 rounded border bg-teal-50 border-teal-200 px-3 py-2 relative hover:shadow-sm transition-shadow">
                              <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                      <StickyNote className="h-3.5 w-3.5 text-teal-700" />
                                      <span className="text-xs font-bold text-teal-700">CalAIM Note</span>
                                      
                                      {/* Badges for Modifiers */}
                                       {(note.pos_name || note.pos_code) && (
                                           <Badge variant="outline" className="h-4 px-1.5 text-xs bg-white/60 text-teal-800 border-teal-200 font-normal whitespace-nowrap">
                                              {note.pos_name || note.pos_code}
                                           </Badge>
                                       )}
                                       {note.proceduretype && (
                                           <Badge variant="outline" className="h-4 px-1.5 text-xs bg-white/60 text-teal-800 border-teal-200 font-normal whitespace-nowrap">
                                               {note.proceduretype}
                                           </Badge>
                                       )}
                                  </div>
                                  
                                  {/* Actions */}
                                  {(canEditCalaimNote || canDeleteCalaimNote) && (
                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                      {canEditCalaimNote && (
                                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-teal-700 hover:bg-teal-200/50 rounded-full" onClick={() => handleEditClick(note)}>
                                          <Edit className="h-3 w-3" />
                                        </Button>
                                      )}
                                      {canDeleteCalaimNote && (
                                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-600 hover:bg-red-100 rounded-full" onClick={() => handleDeleteClick(note.id)}>
                                          <Trash className="h-3 w-3" />
                                        </Button>
                                      )}
                                    </div>
                                  )}
                              </div>
                              
                              {/* Note Content */}
                              <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">
                                  {note.procedurenote || <span className="text-gray-400 italic">No content</span>}
                              </p>
                              
                              {/* Footer Metadata */}
                              <div className="mt-2 flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                                  {note.procedurelength && (
                                      <span className="flex items-center gap-1 bg-white/50 px-1 rounded">
                                          <Clock className="h-3 w-3" /> {note.procedurelength} mins
                                      </span>
                                  )}
                                   <span className="flex items-center gap-1 bg-white/50 px-1 rounded">
                                      <Tag className="h-3 w-3" /> 
                                      {note.proceduremodifier || 
                                                    `${note.proceduremod1 === "1" ? "In Person" : "Telehealth"}, 
                                                     ${note.proceduremod2 === "1" ? "Clinical" : "NonClinical"}, 
                                                     ${note.proceduremod3 === "1" ? "Care" : "Outreach"}`}
                                   </span>
                              </div>
                          </div>
                        </div>
                    ))
                ) : (
                    <div className="text-center py-8 text-xs text-gray-400 flex flex-col items-center justify-center">
                        {loadingNotes ? (
                            <>
                                <Clock className="h-5 w-5 mb-2 animate-spin text-gray-300" />
                                <span>Loading notes...</span>
                            </>
                        ) : (
                            <>
                                <AlertCircle className="h-5 w-5 mb-2 text-gray-300" />
                                <span>No CalAIM notes found.</span>
                            </>
                        )}
                    </div>
                )}
             </div>
          </div>
      </div>

      <AddNoteCalAIM
        isOpen={newNoteModalOpen}
        onClose={() => {
          setNewNoteModalOpen(false);
          refetchNotes();
        }}
        user={user}
        calaimData={calaimData}
        odrNumber={odrNumber.toString()}
        participant={programNumber.toString()}
        refetch={refetchNotes}
      />
            
      <EditCalAIM
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedCalAIM(null);
          refetchNotes();
        }}
        calaimData={selectedCalAIM}
        user={user}
      />

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent className="bg-white p-3 rounded-md shadow-md max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-sm font-medium flex items-center gap-1.5">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              Delete CalAIM Note
            </AlertDialogTitle>
            <AlertDialogDescription className="text-xs text-gray-600">
              Are you sure you want to delete this CalAIM note? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-3">
            <AlertDialogCancel className="h-7 text-xs font-medium" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              className="h-7 text-xs font-medium bg-red-600 hover:bg-red-700"
              onClick={handleDeleteConfirm} 
              disabled={deleteLoading}
            >
              {deleteLoading ? (
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3 animate-spin" />
                  Deleting...
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <Trash className="h-3 w-3" />
                  Delete
                </span>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CalAIM;
